import type { components } from '@/generated/contracts/api-v1';

/**
 * The RFC 9457 body /api/v1 answers an error with, as the contract declares it (ProblemDetails): `code` is
 * stable, `field` names the input at fault, `traceId` finds the request in the backend's logs. Partial, because
 * an error that did not come from /api/v1 (a proxy page, a timeout) carries none of it.
 */
export type ProblemBody = Partial<components['schemas']['ProblemDetails']>;

export type PublishProblemError = NonNullable<components['schemas']['ProblemDetails']['errors']>[number];

const UNREACHABLE = "We couldn't reach our servers. Check your connection and try again.";
const TIMED_OUT = 'The request took too long. Please try again.';
const SERVER_TROUBLE = 'Something went wrong on our side. Please try again in a moment.';

export function rateLimitedMessage(retryAfterSeconds: number | null): string {
  if (!retryAfterSeconds || retryAfterSeconds <= 0) return 'Too many attempts. Please wait a moment and try again.';
  if (retryAfterSeconds <= 90) return `Too many attempts. Please wait ${retryAfterSeconds} seconds and try again.`;
  const minutes = Math.ceil(retryAfterSeconds / 60);
  return `Too many attempts. Please wait ${minutes} minutes and try again.`;
}

function retryAfterOf(response: Response, body: unknown): number | null {
  const fromBody = body && typeof body === 'object' ? (body as { retryAfterSeconds?: unknown }).retryAfterSeconds : undefined;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody)) return Math.ceil(fromBody);
  const header = response.headers.get('Retry-After');
  if (!header) return null;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return Math.ceil(seconds);
  const at = Date.parse(header);
  return Number.isNaN(at) ? null : Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

/**
 * A failed /api/v1 call. An Error, so it can be thrown and logged like any other; `status` is 0
 * when no response arrived (network failure, timeout) or the client refused the call itself.
 */
export class ApiProblem extends Error {
  readonly status: number;
  /** Stable machine-readable reason, such as `not_found`, `forbidden`, `invalid_value`. */
  readonly code: string;
  readonly title: string;
  readonly field: string | null;
  readonly detail: string | null;
  readonly traceId: string | null;
  readonly type: string | null;
  readonly retryAfterSeconds: number | null;
  /** On publish_incomplete: everything the story or its project listing still needs. */
  readonly errors: PublishProblemError[];

  constructor(init: {
    status: number;
    code: string;
    title: string;
    field?: string | null;
    detail?: string | null;
    traceId?: string | null;
    type?: string | null;
    retryAfterSeconds?: number | null;
    errors?: PublishProblemError[];
    cause?: unknown;
  }) {
    super(init.title, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiProblem';
    this.status = init.status;
    this.code = init.code;
    this.title = init.title;
    this.field = init.field ?? null;
    this.detail = init.detail ?? null;
    this.traceId = init.traceId ?? null;
    this.type = init.type ?? null;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.errors = init.errors ?? [];
  }

  /** The problem an error response carries; a body that is not a problem still yields one, coded `http_<status>`. */
  static fromResponse(response: Response, body: unknown): ApiProblem {
    const problem: ProblemBody = body && typeof body === 'object' ? (body as ProblemBody) : {};
    const text = typeof body === 'string' && body.trim() ? body.trim() : null;
    const rateLimited = response.status === 429;
    const retryAfterSeconds = rateLimited ? retryAfterOf(response, body) : null;
    const ownTitle = problem.title || problem.detail || null;
    const title = rateLimited
      ? rateLimitedMessage(retryAfterSeconds)
      : ownTitle || (response.status >= 500 ? SERVER_TROUBLE : text) || `HTTP ${response.status}`;
    return new ApiProblem({
      status: response.status,
      code: typeof problem.code === 'string' ? problem.code : rateLimited ? 'rate_limited' : `http_${response.status}`,
      title,
      field: typeof problem.field === 'string' ? problem.field : null,
      detail: problem.detail ?? null,
      traceId: typeof problem.traceId === 'string' ? problem.traceId : null,
      type: problem.type ?? null,
      retryAfterSeconds,
      errors: Array.isArray(problem.errors) ? problem.errors : [],
    });
  }

  /** A call that produced no usable response: the network failed, it timed out, or the body could not be read. */
  static fromError(error: unknown): ApiProblem {
    const name = error instanceof Error || error instanceof DOMException ? (error as Error).name : '';
    const title = name === 'AbortError' || name === 'TimeoutError'
      ? TIMED_OUT
      : error instanceof TypeError
        ? UNREACHABLE
        : error instanceof Error ? error.message : 'Unknown error';
    return new ApiProblem({
      status: 0,
      code: 'network_error',
      title,
      cause: error,
    });
  }
}
